import { useCallback, useState, useMemo, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  IconButton, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { 
  Add as AddIcon, 
  LocationOn as LocationIcon, 
  Close as CloseIcon,
  People as PeopleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { UploadFile as UploadFileIcon } from '@mui/icons-material';
import { FaDrawPolygon as PolygonIcon } from 'react-icons/fa';
import MapComponent from '../components/common/Map';
import { getClientsByPolygon, getPolygonStats } from '../services/mapService';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend, LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';

// Color palette for polygons
const POLYGON_COLORS = [
  '#1976d2', // Blue
  '#d32f2f', // Red
  '#388e3c', // Green
  '#f57c00', // Orange
  '#7b1fa2', // Purple
  '#0097a7', // Cyan
  '#c2185b', // Pink
  '#5d4037', // Brown
  '#455a64', // Blue Grey
  '#fbc02d', // Yellow
];

// Color palette for channel markers
const CHANNEL_COLORS = {
  'SELF SERVICE': '#FF5722',      // Deep Orange
  'EMERGENTES': '#2196F3',       // Blue
  'ALMACENES': '#4CAF50',           // Green
  'KIOSCOS': '#9C27B0',         // Purple
  'HORECA': '#FF9800',            // Orange
  'E-COMMERCE': '#00BCD4',        // Cyan
  'INSTITUCIONAL': '#795548',     // Brown
  'default': '#E91E63'            // Blue Grey (default)
};

// Function to get color for a channel
const getChannelColor = (channel) => {
  return CHANNEL_COLORS[channel] || CHANNEL_COLORS['default'];
};

const MapPage = () => {
  // State for locations
  const [locations, setLocations] = useState([
    { 
      id: 1,
      lat: -34.6037, 
      lng: -58.3816, 
      label: 'Oficina Central',
      address: 'Av. Corrientes 1234, C1043 CABA',
      description: 'Sede principal de la empresa',
      clientCount: 42
    },
    { 
      id: 2,
      lat: -34.6184, 
      lng: -58.3732, 
      label: 'Sucursal Microcentro',
      address: 'Florida 100, C1005 CABA',
      description: 'Sucursal en el centro financiero',
      clientCount: 28
    },
  ]);

  // State for UI
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [polygons, setPolygons] = useState([]);
  const [clients, setClients] = useState([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [selectedPolygon, setSelectedPolygon] = useState(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [selectedChannel, setSelectedChannel] = useState('all');
  const [availableChannels, setAvailableChannels] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [enabledPlaceTypes, setEnabledPlaceTypes] = useState([]);
  const [statsOpen, setStatsOpen] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState('');
  const [polygonStats, setPolygonStats] = useState(null);
  const [newLocation, setNewLocation] = useState({
    label: '',
    address: '',
    description: ''
  });
  const [renameOpen, setRenameOpen] = useState(false);
  const [renamePolygonId, setRenamePolygonId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const kmlInputRef = useRef(null);

  // Chart data transforms
  const pieData = useMemo(() => {
    if (!polygonStats?.summary) return [];
    return polygonStats.summary.map((s, idx) => ({
      name: s.category,
      value: Number(s.qty) || 0,
      color: POLYGON_COLORS[idx % POLYGON_COLORS.length]
    }));
  }, [polygonStats]);

  const { lineData, lineCategories } = useMemo(() => {
    const daily = polygonStats?.daily || [];
    if (!daily.length) return { lineData: [], lineCategories: [] };
    const categories = [...new Set(daily.map(d => d.category))];
    // Group by date_part
    const byDay = new Map();
    daily.forEach(d => {
      const day = Number(d.date_part);
      const key = isNaN(day) ? d.date_part : day;
      if (!byDay.has(key)) byDay.set(key, { day: key });
      const row = byDay.get(key);
      row[d.category] = (row[d.category] || 0) + (Number(d.qty) || 0);
    });
    // Sort by day
    const rows = Array.from(byDay.values()).sort((a, b) => a.day - b.day);
    return { lineData: rows, lineCategories: categories };
  }, [polygonStats]);

  // Event handlers
  const handleMarkerClick = useCallback((location) => {
    setSelectedLocation(location);
    setSelectedPolygon(null);
  }, []);

  const handlePolygonComplete = useCallback(async (coordinates) => {
    // Assign color based on polygon index
    const colorIndex = polygons.length % POLYGON_COLORS.length;
    const polygonColor = POLYGON_COLORS[colorIndex];
    
    const newPolygon = {
      id: Date.now(),
      coordinates,
      name: `Área ${polygons.length + 1}`,
      clientCount: 0,
      timestamp: new Date().toISOString(),
      color: polygonColor
    };
    
    try {
      setIsLoadingClients(true);
      const token = localStorage.getItem('token');
      console.log('Calling getClientsByPolygon with coordinates:', coordinates);
      const response = await getClientsByPolygon(coordinates);
      console.log('API Response:', response);
      
      if (response && response.success) {
        const clients = response.data || [];
        const clientCount = clients.length;
        
        // Extract unique channels
        const channels = [...new Set(clients.map(client => client.channel).filter(Boolean))];
        setAvailableChannels(channels);
        setSelectedChannel('all');
        
        const updatedPolygon = {
          ...newPolygon,
          clientCount,
          clients: clients,
          color: polygonColor
        };
        
        setPolygons(prev => [...prev, updatedPolygon]);
        setSelectedPolygon(updatedPolygon);
        setClients(clients);
        
        // Show the response message from the API
        setSnackbar({
          open: true,
          message: response.res || `Se encontraron ${clientCount} clientes en el área seleccionada`,
          severity: 'success'
        });
      } else {
        throw new Error('No se encontraron clientes en el área seleccionada');
      }
    } catch (error) {
      console.error('Error fetching clients in polygon:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Error al buscar clientes en el área seleccionada',
        severity: 'error'
      });
    } finally {
      setIsLoadingClients(false);
    }
  }, [polygons.length]);

  const handlePolygonClick = useCallback((polygon) => {
    setSelectedPolygon(polygon);
    setSelectedLocation(null);
    setClients(polygon.clients || []);
    
    // Extract unique channels from polygon clients
    const clients = polygon.clients || [];
    const channels = [...new Set(clients.map(client => client.channel).filter(Boolean))];
    setAvailableChannels(channels);
    setSelectedChannel('all');
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleDeletePolygon = useCallback((id, event) => {
    event?.stopPropagation();
    const newPolygons = polygons.filter(p => p.id !== id);
    setPolygons(newPolygons);
    
    if (selectedPolygon?.id === id) {
      setSelectedPolygon(null);
      setClients([]);
      // Close stats dialog and reset stats if open for this polygon
      setStatsOpen(false);
      setPolygonStats(null);
      setStatsError('');
    }
    
    setSnackbar({
      open: true,
      message: 'Área eliminada',
      severity: 'info'
    });
  }, [polygons, selectedPolygon]);

  const handleOpenRename = useCallback((polygon, event) => {
    event?.stopPropagation();
    setRenamePolygonId(polygon.id);
    setRenameValue(polygon.name || '');
    setRenameOpen(true);
  }, []);

  const handleSaveRename = useCallback(() => {
    if (!renamePolygonId) return;
    const updated = polygons.map(p => p.id === renamePolygonId ? { ...p, name: renameValue } : p);
    setPolygons(updated);
    if (selectedPolygon?.id === renamePolygonId) {
      setSelectedPolygon(prev => prev ? { ...prev, name: renameValue } : prev);
    }
    setRenameOpen(false);
    setRenamePolygonId(null);
    setRenameValue('');
  }, [polygons, renamePolygonId, renameValue, selectedPolygon]);

  const handleAddLocation = () => {
    const newLoc = {
      id: Date.now(),
      lat: -34.6037 + (Math.random() * 0.02 - 0.01),
      lng: -58.3816 + (Math.random() * 0.02 - 0.01),
      ...newLocation,
      clientCount: Math.floor(Math.random() * 100)
    };
    
    setLocations([...locations, newLoc]);
    setNewLocation({ label: '', address: '', description: '' });
    setIsAddDialogOpen(false);
  };

  // Calculate filtered clients for display
  const filteredClients = useMemo(() => {
    if (!selectedPolygon) return [];
    return selectedChannel === 'all' 
      ? clients 
      : clients.filter(client => client.channel === selectedChannel);
  }, [clients, selectedChannel, selectedPolygon]);

  // Convert filtered clients to map markers
  const clientMarkers = useMemo(() => {
    return filteredClients
      .filter(client => client.latitude && client.longitude)
      .map(client => ({
        id: `client-${client.id}`,
        lat: parseFloat(client.latitude),
        lng: parseFloat(client.longitude),
        label: client.name,
        address: client.channel || 'Sin canal',
        description: `GEC: ${client.gec || 'N/A'}`,
        clientCount: 0,
        isClient: true,
        clientData: client,
        channelColor: getChannelColor(client.channel)
      }));
  }, [filteredClients]);

  const handleOpenStats = useCallback(async () => {
    if (!selectedPolygon) return;
    setStatsOpen(true);
    setStatsLoading(true);
    setStatsError('');
    try {
      const resp = await getPolygonStats(selectedPolygon.coordinates);
      if (!resp?.success) throw new Error('No se pudieron obtener estadísticas');
      setPolygonStats(resp.stats || { summary: [], daily: [] });
    } catch (e) {
      console.error(e);
      setStatsError(e?.response?.data?.message || e?.message || 'Error al obtener estadísticas');
    } finally {
      setStatsLoading(false);
    }
  }, [selectedPolygon]);

  // Parse a KML string and extract polygons (outer boundaries only)
  const parseKMLtoPolygons = useCallback((kmlText) => {
    try {
      const parser = new DOMParser();
      const xml = parser.parseFromString(kmlText, 'text/xml');
      const polygons = [];

      // Handle <Polygon> nodes
      const polygonNodes = Array.from(xml.getElementsByTagName('Polygon'));
      polygonNodes.forEach((polyNode) => {
        const outer = polyNode.getElementsByTagName('outerBoundaryIs')[0];
        const linear = outer?.getElementsByTagName('LinearRing')[0];
        const coordsNode = linear?.getElementsByTagName('coordinates')[0];
        const coordsText = coordsNode?.textContent || '';
        const path = coordsText
          .trim()
          .split(/\s+/)
          .map(pair => pair.split(',').slice(0, 2))
          .filter(([lon, lat]) => lon && lat)
          .map(([lon, lat]) => ({ lat: parseFloat(lat), lng: parseFloat(lon) }));
        if (path.length) polygons.push(path);
      });

      // Handle MultiGeometry with Polygons inside
      const mgNodes = Array.from(xml.getElementsByTagName('MultiGeometry'));
      mgNodes.forEach((mg) => {
        const polys = Array.from(mg.getElementsByTagName('Polygon'));
        polys.forEach((polyNode) => {
          const outer = polyNode.getElementsByTagName('outerBoundaryIs')[0];
          const linear = outer?.getElementsByTagName('LinearRing')[0];
          const coordsNode = linear?.getElementsByTagName('coordinates')[0];
          const coordsText = coordsNode?.textContent || '';
          const path = coordsText
            .trim()
            .split(/\s+/)
            .map(pair => pair.split(',').slice(0, 2))
            .filter(([lon, lat]) => lon && lat)
            .map(([lon, lat]) => ({ lat: parseFloat(lat), lng: parseFloat(lon) }));
          if (path.length) polygons.push(path);
        });
      });

      return polygons;
    } catch (e) {
      console.error('Error parsing KML:', e);
      return [];
    }
  }, []);

  // Trigger file input
  const handleImportKMLClick = useCallback(() => {
    kmlInputRef.current?.click();
  }, []);

  // Handle file selection and import polygons
  const handleKMLFileChange = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const paths = parseKMLtoPolygons(text);
      if (!paths.length) {
        setSnackbar({ open: true, message: 'No se encontraron polígonos en el KML', severity: 'warning' });
        return;
      }
      // Import each polygon sequentially to keep color order and API calls stable
      for (const coords of paths) {
        // Reuse the existing flow to create polygon, fetch clients and update UI
        // eslint-disable-next-line no-await-in-loop
        await handlePolygonComplete(coords);
      }
      setSnackbar({ open: true, message: `Importadas ${paths.length} área(s) desde KML`, severity: 'success' });
    } catch (e) {
      console.error('Error importing KML:', e);
      setSnackbar({ open: true, message: 'Error al importar el archivo KML', severity: 'error' });
    } finally {
      // reset input to allow re-uploading the same file
      if (kmlInputRef.current) kmlInputRef.current.value = '';
    }
  }, [handlePolygonComplete, parseKMLtoPolygons, setSnackbar]);

  // Combine original locations with client markers
  const allMarkers = useMemo(() => {
    return [...locations, ...clientMarkers];
  }, [locations, clientMarkers]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <div>
            <Typography variant="h5" gutterBottom>
              Mapa de Clientes
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Visualiza y analiza la distribución de clientes
            </Typography>
          </div>
          <Box display="flex" gap={1}>
            <Button 
              variant={isDrawing ? 'outlined' : 'contained'}
              color={isDrawing ? 'error' : 'primary'}
              startIcon={<PolygonIcon />}
              onClick={() => setIsDrawing(!isDrawing)}
              size="small"
            >
              {isDrawing ? 'Finalizar dibujo' : 'Dibujar área'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={handleImportKMLClick}
              size="small"
            >
              Importar KML
            </Button>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="places-layer-label">Capas</InputLabel>
              <Select
                labelId="places-layer-label"
                multiple
                value={enabledPlaceTypes}
                onChange={(e) => setEnabledPlaceTypes(e.target.value)}
                label="Capas"
                renderValue={(selected) => selected.length ? `${selected.length} capas` : 'Ninguna'}
              >
                <MenuItem value="bus_station">Paradas de colectivos</MenuItem>
                <MenuItem value="restaurant">Restaurantes</MenuItem>
                <MenuItem value="gym">Gimnasios</MenuItem>
                <MenuItem value="supermarket">Supermercados</MenuItem>
                <MenuItem value="school">Escuelas</MenuItem>
                <MenuItem value="subway_station">Estaciones de subte</MenuItem>
              </Select>
            </FormControl>
            <Button 
              variant="outlined" 
              startIcon={<AddIcon />}
              onClick={() => setIsAddDialogOpen(true)}
              size="small"
            >
              Agregar
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar - Areas */}
        <Box sx={{ 
          width: isSidebarCollapsed ? 48 : 300, 
          borderRight: 1, 
          borderColor: 'divider', 
          display: 'flex', 
          flexDirection: 'column',
          transition: 'width 0.3s ease',
          position: 'relative'
        }}>
          <Paper square sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {!isSidebarCollapsed && (
              <Typography variant="subtitle1" fontWeight="medium">Ubicaciones</Typography>
            )}
            <IconButton 
              size="small" 
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              sx={{ ml: isSidebarCollapsed ? 0 : 'auto' }}
            >
              {isSidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Paper>
          {!isSidebarCollapsed && (
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              <List dense>
                {locations.map((location) => (
                  <ListItem 
                  key={location.id}
                  button
                  selected={selectedLocation?.id === location.id}
                  onClick={() => handleMarkerClick(location)}
                >
                  <ListItemIcon>
                    <LocationIcon color={selectedLocation?.id === location.id ? 'primary' : 'inherit'} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={location.label}
                    secondary={`${location.clientCount} clientes`}
                    secondaryTypographyProps={{
                      variant: 'caption',
                      color: selectedLocation?.id === location.id ? 'primary' : 'text.secondary',
                      sx: {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }
                    }}
                  />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          
          {!isSidebarCollapsed && (
            <Paper square sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle1" fontWeight="medium">Áreas Dibujadas</Typography>
            </Paper>
          )}
          {!isSidebarCollapsed && (
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {polygons.length > 0 ? (
                <List dense>
                  {polygons.map((polygon) => (
                    <ListItem 
                      key={polygon.id}
                      button
                      selected={selectedPolygon?.id === polygon.id}
                      onClick={() => handlePolygonClick(polygon)}
                      secondaryAction={
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton 
                            edge="end" 
                            size="small"
                            onClick={(e) => handleOpenRename(polygon, e)}
                            title="Editar nombre"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            edge="end" 
                            size="small"
                            onClick={(e) => handleDeletePolygon(polygon.id, e)}
                            title="Eliminar área"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      }
                      sx={{
                        '&.Mui-selected': {
                          backgroundColor: 'primary.light',
                          color: 'primary.contrastText',
                          '&:hover': {
                            backgroundColor: 'primary.light',
                          },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Box sx={{ 
                          width: 16, 
                          height: 16, 
                          borderRadius: '2px', 
                          backgroundColor: polygon.color || '#1976d2',
                          border: '2px solid',
                          borderColor: selectedPolygon?.id === polygon.id ? 'white' : 'rgba(0,0,0,0.2)',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                        }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" noWrap>
                              {polygon.name}
                            </Typography>
                          </Box>
                        } 
                        secondary={`${polygon.clientCount} clientes`}
                        secondaryTypographyProps={{
                          variant: 'caption',
                          color: selectedPolygon?.id === polygon.id ? 'primary.contrastText' : 'text.secondary'
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No hay áreas dibujadas. Usa el botón "Dibujar área" para comenzar.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Map */}
        <Box sx={{ flex: 1, position: 'relative' }}>
          <MapComponent 
            locations={allMarkers}
            onMarkerClick={handleMarkerClick}
            onPolygonComplete={handlePolygonComplete}
            onPolygonSelect={handlePolygonClick}
            drawingMode={isDrawing}
            onDrawingModeChange={setIsDrawing}
            polygons={polygons}
            selectedLocation={selectedLocation}
            selectedPolygon={selectedPolygon}
            availableChannels={availableChannels}
            channelColors={CHANNEL_COLORS}
            enabledPlaceTypes={enabledPlaceTypes}
          />
        </Box>

        {/* Details Panel */}
        <Box sx={{ width: 350, borderLeft: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
          <Paper square sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle1" fontWeight="medium">
              {selectedPolygon 
                ? 'Detalles del Área' 
                : selectedLocation 
                  ? 'Detalles de Ubicación' 
                  : 'Información'}
            </Typography>
          </Paper>
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {isLoadingClients ? (
              <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  Cargando clientes...
                </Typography>
              </Box>
            ) : selectedPolygon ? (
              <Box>
                <Box mb={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box sx={{ 
                        width: 20, 
                        height: 20, 
                        borderRadius: '4px', 
                        backgroundColor: selectedPolygon.color || '#1976d2',
                        border: '2px solid rgba(0,0,0,0.2)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }} />
                      <Typography variant="h6">{selectedPolygon.name}</Typography>
                    </Box>
                    <Chip 
                      label={`${selectedPolygon.clientCount} clientes`}
                      size="small"
                      sx={{ 
                        backgroundColor: selectedPolygon.color || '#1976d2',
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {selectedPolygon.coordinates.length} puntos • {new Date(selectedPolygon.timestamp).toLocaleString()}
                  </Typography>
                </Box>

                {/* Channel Filter */}
                {availableChannels.length > 0 && (
                  <Box mb={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="channel-filter-label">Filtrar por Canal</InputLabel>
                      <Select
                        labelId="channel-filter-label"
                        id="channel-filter"
                        value={selectedChannel}
                        label="Filtrar por Canal"
                        onChange={(e) => setSelectedChannel(e.target.value)}
                      >
                        <MenuItem value="all">Todos los canales</MenuItem>
                        {availableChannels.map((channel) => (
                          <MenuItem key={channel} value={channel}>
                            {channel}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}

                {filteredClients.length > 0 ? (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Mostrando {filteredClients.length} de {clients.length} clientes
                    </Typography>
                    <TableContainer sx={{ maxHeight: 300 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Nombre</TableCell>
                            <TableCell>Canal</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredClients.map((client, index) => (
                            <TableRow key={index} hover>
                              <TableCell>{client.id}</TableCell>
                              <TableCell>{client.name}</TableCell>
                              <TableCell>{client.channel}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    {/* Apply Promo Button */}
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                      <Button
                        variant="contained"
                        fullWidth
                        sx={{
                          backgroundColor: selectedPolygon.color || '#1976d2',
                          '&:hover': {
                            backgroundColor: selectedPolygon.color || '#1565c0',
                            filter: 'brightness(0.9)',
                          },
                          fontWeight: 'bold',
                          py: 1.5
                        }}
                        onClick={() => {
                          setSnackbar({
                            open: true,
                            message: `Promo aplicada al área seleccionada: ${selectedPolygon.name}`,
                            severity: 'success'
                          });
                        }}
                      >
                        Aplicar PROMO
                      </Button>
                    </Box>
                    {/* View Statistics Button */}
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                      <Button
                        variant="outlined"
                        fullWidth
                        sx={{
                          borderColor: selectedPolygon.color || '#1976d2',
                          color: selectedPolygon.color || '#1976d2',
                          fontWeight: 'bold',
                          py: 1.25
                        }}
                        onClick={handleOpenStats}
                      >
                        Ver estadísticas
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {selectedChannel === 'all' 
                        ? 'No se encontraron clientes en esta área'
                        : `No se encontraron clientes en el canal "${selectedChannel}"`
                      }
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : selectedLocation ? (
              <Box>
                <Typography variant="h6" gutterBottom>{selectedLocation.label}</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {selectedLocation.address}
                </Typography>
                <Typography variant="body1" sx={{ mt: 2, fontWeight: 'medium' }}>
                  {selectedLocation.clientCount} clientes
                </Typography>
                {selectedLocation.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    {selectedLocation.description}
                  </Typography>
                )}
              </Box>
            ) : (
              <Box sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                textAlign: 'center', 
                p: 3 
              }}>
                <PeopleIcon sx={{ fontSize: 48, color: 'action.active', mb: 2, opacity: 0.7 }} />
                <Typography variant="body1" color="text.secondary">
                  Selecciona una ubicación o dibuja un área en el mapa para ver los clientes
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Add Location Dialog */}
      <Dialog open={isAddDialogOpen} onClose={() => setIsAddDialogOpen(false)}>
        <DialogTitle>Agregar Nueva Ubicación</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 400, pt: 2 }}>
            <TextField
              label="Nombre"
              value={newLocation.label}
              onChange={(e) => setNewLocation(prev => ({ ...prev, label: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label="Dirección"
              value={newLocation.address}
              onChange={(e) => setNewLocation(prev => ({ ...prev, address: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label="Descripción"
              value={newLocation.description}
              onChange={(e) => setNewLocation(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={3}
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddDialogOpen(false)}>Cancelar</Button>
          <Button 
            onClick={handleAddLocation} 
            variant="contained"
            disabled={!newLocation.label || !newLocation.address}
          >
            Agregar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Polygon Dialog */}
      <Dialog open={renameOpen} onClose={() => setRenameOpen(false)}>
        <DialogTitle>Editar nombre del área</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, minWidth: 360 }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Nombre"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)}>Cancelar</Button>
          <Button 
            onClick={handleSaveRename} 
            variant="contained"
            disabled={!renameValue.trim()}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Statistics Dialog */}
      <Dialog open={statsOpen} onClose={() => setStatsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Estadísticas del área {selectedPolygon ? `- ${selectedPolygon.name}` : ''}</DialogTitle>
        <DialogContent dividers>
          {statsLoading ? (
            <Box display="flex" alignItems="center" justifyContent="center" py={6}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                Cargando estadísticas...
              </Typography>
            </Box>
          ) : statsError ? (
            <Alert severity="error">{statsError}</Alert>
          ) : (!polygonStats || (!polygonStats.summary?.length && !polygonStats.daily?.length)) ? (
            <Typography variant="body2" color="text.secondary">No hay datos para mostrar.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
              {/* Pie chart for summary */}
              {pieData.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>Resumen por categoría</Typography>
                  <Box sx={{ width: '100%', height: 260 }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={90} label>
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              )}

              {/* Line chart for daily */}
              {lineData.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>Evolución diaria</Typography>
                  <Box sx={{ width: '100%', height: 300 }}>
                    <ResponsiveContainer>
                      <LineChart data={lineData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="day" tickFormatter={(v) => `${v}`} />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Legend />
                        {lineCategories.map((cat, idx) => (
                          <Line key={cat} type="monotone" dataKey={cat} stroke={POLYGON_COLORS[idx % POLYGON_COLORS.length]} strokeWidth={2} dot={false} />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatsOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      {/* Hidden file input for KML import */}
      <input
        ref={kmlInputRef}
        type="file"
        accept=".kml,application/vnd.google-earth.kml+xml"
        onChange={handleKMLFileChange}
        style={{ display: 'none' }}
      />
    </Box>
  );
};

export default MapPage;
